import { lazy, type ComponentType } from 'react';

/**
 * Route-level lazy() that survives stale deploys.
 *
 * After a publish, a browser holding an old index.html asks for chunk
 * filenames that no longer exist -> dynamic import rejects -> the route
 * error boundary paints "Something went wrong". This is especially common
 * on mobile, where tabs live for days.
 *
 * Strategy: retry the import once (transient network), then force a single
 * hard reload (guarded by sessionStorage so we can never loop) so the
 * browser picks up the fresh manifest.
 */
const RELOAD_KEY = 'psyforge:chunk-reloaded';

function isChunkLoadError(error: unknown): boolean {
  const message = String((error as Error)?.message ?? error ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(message)
  );
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* private mode */
      }
      return mod;
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      // One quick retry — covers flaky mobile networks.
      try {
        return await factory();
      } catch (retryError) {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === '1';
          sessionStorage.setItem(RELOAD_KEY, '1');
        } catch {
          /* private mode: fall through to throw */
        }
        if (!alreadyReloaded) {
          window.location.reload();
          // Keep Suspense pending while the reload happens.
          return await new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
